import { averageSpeed, slipPercent, steamingHours, theoreticalSpeed } from './calc.js';
import { SIDE_KINDS } from './next-operation.js';
import type { VoyageReportKind } from './schemas.js';

/**
 * Noon ve günlük raporların panelleri (ADR 0059 §4, §5, §7; ortağın `g3.jpeg` ve `g1.jpeg`):
 * COSP referansı, önceki rapor, seferde koşulan mesafe, ETA önerisi, CP durumu, iki slip, Berthing
 * (demir, drift başlangıcı) referansı, kalan yük. Hepsi türetilir, saklanmaz; rapor, form ve PDF
 * aynı seferin raporlarından aynı sonucu çıkarır. Saf fonksiyonlar; raporları çağıran süzer
 * (gönderilmiş, silinmemiş).
 */

export interface PanelReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  tzOffsetMin: number;
  lat: number | null;
  lon: number | null;
  data: Readonly<Record<string, unknown>>;
}

const round = (value: number, digits = 3) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};
const numberOf = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const textOf = (value: unknown) => (typeof value === 'string' && value.trim() ? value : null);
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const time = (iso: string) => Date.parse(iso);
const sorted = (reports: readonly PanelReport[]) =>
  [...reports].sort((a, b) => time(a.reportedAt) - time(b.reportedAt));

/** Raporun "son rapordan beri" mesafesi (Noon'da seyir bölümünde; `calc.ts` ile aynı yol). */
function distanceOf(report: PanelReport): number {
  const source = report.kind === 'noon' ? record(report.data.navigation) : report.data;
  return numberOf(source.distanceSinceLast) ?? 0;
}

/** Rapor anındaki değerler (formdaki, henüz kaydedilmemiş olabilir). */
export interface PanelCurrent {
  reportedAt: string;
  /** Bu raporun "son rapordan beri" mesafesi. */
  distanceNm: number | null;
}

export interface CospReference {
  cosp: {
    id: string;
    reportedAt: string;
    tzOffsetMin: number;
    lat: number | null;
    lon: number | null;
  };
  hours: number | null;
  distanceNm: number;
  avgSpeedKn: number | null;
}

/**
 * COSP referansı (`g3.jpeg` §2): rapordan önceki son COSP ve o seyir sürüyorsa (arada EOSP yoksa)
 * COSP'tan beri süre, mesafe (aradaki raporlar + bu rapor) ve ortalama hız. Brüttür: seyir içi
 * demir, drift ve boğaz düşülmez (EOSP incelemesiyle aynı; ADR 0058 §6).
 */
export function cospReference(
  reports: readonly PanelReport[],
  current: PanelCurrent,
): CospReference | null {
  const end = time(current.reportedAt);
  const before = sorted(reports).filter((r) => time(r.reportedAt) < end);
  const index = before.findLastIndex((r) => r.kind === 'cosp');
  const cosp = before[index];
  if (!cosp) return null;
  const after = before.slice(index + 1);
  if (after.some((r) => r.kind === 'eosp')) return null;
  const distanceNm = round(
    after.reduce((sum, r) => sum + distanceOf(r), 0) + (current.distanceNm ?? 0),
  );
  const hours = steamingHours(cosp.reportedAt, current.reportedAt);
  return {
    cosp: {
      id: cosp.id,
      reportedAt: cosp.reportedAt,
      tzOffsetMin: cosp.tzOffsetMin,
      lat: cosp.lat,
      lon: cosp.lon,
    },
    hours,
    distanceNm,
    avgSpeedKn: averageSpeed(distanceNm, hours),
  };
}

export interface PreviousReport {
  id: string;
  kind: VoyageReportKind;
  reportedAt: string;
  tzOffsetMin: number;
  hours: number | null;
}

/**
 * Önceki rapor (`g3.jpeg` §3, `g1.jpeg` §3 "Time Since Last Report"): rapordan önceki son akış
 * raporu; yan olaylar (yük, kiralama) sayılmaz.
 */
export function previousFlowReport(
  reports: readonly PanelReport[],
  at: string,
): PreviousReport | null {
  const previous = sorted(reports)
    .filter((r) => !SIDE_KINDS.has(r.kind) && time(r.reportedAt) < time(at))
    .at(-1);
  if (!previous) return null;
  return {
    id: previous.id,
    kind: previous.kind,
    reportedAt: previous.reportedAt,
    tzOffsetMin: previous.tzOffsetMin,
    hours: steamingHours(previous.reportedAt, at),
  };
}

/** Seferde koşulan mesafe: rapordan önceki bütün "son rapordan beri" mesafeleri + bu rapor. */
export function voyageDistanceRun(reports: readonly PanelReport[], current: PanelCurrent): number {
  const end = time(current.reportedAt);
  return round(
    reports.filter((r) => time(r.reportedAt) < end).reduce((sum, r) => sum + distanceOf(r), 0) +
      (current.distanceNm ?? 0),
  );
}

/**
 * ETA önerisi: rapor zamanı + kalan mesafe / hız (dakikaya yuvarlı). Yalnız öneridir; ETA alanı
 * kaptanın girdiğidir.
 */
export function etaSuggestion(
  reportedAt: string,
  distanceToGoNm: number | null,
  speedKn: number | null,
): string | null {
  if (distanceToGoNm === null || speedKn === null || speedKn <= 0) return null;
  const at = time(reportedAt);
  if (Number.isNaN(at)) return null;
  const minutes = Math.round((distanceToGoNm / speedKn) * 60);
  return new Date(at + minutes * 60_000).toISOString();
}

/** CP durumu (`g3.jpeg` §13 "Charter Party Status"): rapordan önceki son On / Off Hire olayı. */
export function charterStatus(
  reports: readonly PanelReport[],
  at: string,
): 'on_hire' | 'off_hire' | null {
  const last = sorted(reports)
    .filter(
      (r) => (r.kind === 'on_hire' || r.kind === 'off_hire') && time(r.reportedAt) <= time(at),
    )
    .at(-1);
  return last ? (last.kind as 'on_hire' | 'off_hire') : null;
}

/** Hız farkının istenen hıza oranı (%; `g3.jpeg` "+1.02 kt (+8.5%)"). */
export function speedDiffPct(plannedKn: number | null, diffKn: number | null): number | null {
  return plannedKn && diffKn !== null ? round((diffKn / plannedKn) * 100, 1) : null;
}

export interface SlipPanel {
  /** Motor (pervane) hızı = pitch × RPM × 60 / 1852. */
  engineSpeedKn: number | null;
  /** Slip (STW): bugünkü değer (ADR 0024 #14). */
  slipStwPct: number | null;
  /** Slip (SOG): aynı formül yere göre hızla (ADR 0059 §7). */
  slipSogPct: number | null;
  /** Akıntı etkisi STW − SOG (kn); ortağın (STW − SOG) / STW formülü (%). */
  currentEffectKn: number | null;
  currentEffectPct: number | null;
}

/** İki slip ve akıntı etkisi (ADR 0059 §7); eksik girdide `null`. */
export function slipPanel(input: {
  pitchM: number | null;
  rpm: number | null;
  stw: number | null;
  sog: number | null;
}): SlipPanel {
  const { pitchM, rpm, stw, sog } = input;
  const engine = pitchM !== null && rpm !== null ? theoreticalSpeed(pitchM, rpm) : null;
  const effect = stw !== null && sog !== null ? round(stw - sog, 2) : null;
  return {
    engineSpeedKn: engine,
    slipStwPct: engine !== null && stw !== null ? slipPercent(engine, stw) : null,
    slipSogPct: engine !== null && sog !== null ? slipPercent(engine, sog) : null,
    currentEffectKn: effect,
    currentEffectPct: effect !== null && stw ? round((effect / stw) * 100, 2) : null,
  };
}

export interface PhaseReference {
  kind: 'arrival' | 'anchorage_start' | 'drift_start';
  id: string;
  reportedAt: string;
  tzOffsetMin: number;
  lat: number | null;
  lon: number | null;
  /** Berthing: liman, terminal, yanaşma tarafı, ilk halat, all fast, pilot off, römorkörler. */
  port: string | null;
  portCode: string | null;
  terminal: string | null;
  berthSide: string | null;
  firstLine: string | null;
  allFast: string | null;
  pilotOff: string | null;
  tugs: string[];
  /** Demir ve drift başlangıcının nedeni. */
  reason: string | null;
}

const PHASE_START: Record<string, PhaseReference['kind']> = {
  port_daily: 'arrival',
  anchorage_daily: 'anchorage_start',
  drift_daily: 'drift_start',
};
/** Aşamayı bitiren raporlar: bunlardan sonra eski başlangıç sayılmaz. */
const PHASE_END: Record<PhaseReference['kind'], readonly VoyageReportKind[]> = {
  arrival: ['departure'],
  anchorage_start: ['anchorage_end'],
  drift_start: ['drift_end'],
};

/**
 * Günlüğün aşama referansı (`g1.jpeg` §2 "From Arrival Report (auto)", ADR 0059 §4): Port
 * Daily'de son Berthing, Anchorage Daily'de demirleme başlangıcı, Drift Daily'de drift başlangıcı;
 * aşama bitmişse (kalkış, Anchor Up, Drift End) `null`.
 */
export function phaseReference(
  kind: VoyageReportKind,
  reports: readonly PanelReport[],
  at: string,
): PhaseReference | null {
  const startKind = PHASE_START[kind];
  if (!startKind) return null;
  const before = sorted(reports).filter((r) => time(r.reportedAt) <= time(at));
  const index = before.findLastIndex((r) => r.kind === startKind);
  const start = before[index];
  if (!start) return null;
  if (before.slice(index + 1).some((r) => PHASE_END[startKind].includes(r.kind))) return null;
  const data = start.data;
  const tugs = Array.isArray(data.tugs)
    ? data.tugs.map((row) => textOf(record(row).name)).filter((name): name is string => !!name)
    : [];
  return {
    kind: startKind,
    id: start.id,
    reportedAt: start.reportedAt,
    tzOffsetMin: start.tzOffsetMin,
    lat: start.lat,
    lon: start.lon,
    port: textOf(data.port),
    portCode: textOf(data.portCode),
    terminal: textOf(data.terminal),
    berthSide: textOf(data.berthSide),
    firstLine: textOf(data.firstLine),
    allFast: textOf(data.allFast) ?? (startKind === 'arrival' ? start.reportedAt : null),
    pilotOff: textOf(data.pilotOff),
    tugs,
    reason: textOf(data.reason),
  };
}

/** Kalan yük = planlanan − toplam (`g1.jpeg` §5 "Remaining Quantity"); eksikse `null`. */
export function cargoRemaining(
  cargo: { plannedQty?: unknown; handledTotalQty?: unknown } | null | undefined,
): number | null {
  const planned = numberOf(cargo?.plannedQty);
  const total = numberOf(cargo?.handledTotalQty);
  return planned !== null && total !== null ? round(planned - total) : null;
}
